import { useEffect, useRef, useState } from 'react'

/**
 * Poll a backend resource until it reaches a terminal state, then stop.
 *
 * Polling, not animation: the view renders whatever the API currently reports —
 * including stages the backend honestly marks `skipped` with a reason — and the
 * loop ends when the work does. The brief is explicit that a progress view must
 * show the real pipeline and never a spinner that keeps turning after the
 * backend has stopped, and this hook is the one place that property lives.
 *
 * A transient failure does NOT end the loop: it is surfaced as `error` and
 * retried more slowly, because a network blip must not freeze the view at
 * whatever it happened to show.
 */
const INTERVAL_MS = 800

export interface PollingState<T> {
  data: T | null
  error: string | null
  /** True while the resource is still moving. */
  polling: boolean
}

export function usePolling<T>(
  /** Null pauses the loop entirely. Changing it restarts it. */
  key: string | null,
  fetcher: (signal: AbortSignal) => Promise<T>,
  isTerminal: (value: T) => boolean,
  initial: T | null = null,
): PollingState<T> {
  const [data, setData] = useState<T | null>(initial)
  const [error, setError] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // The fetcher is a fresh closure on every render; keeping it in a ref means
  // the effect below depends on `key` alone and the loop is not restarted once
  // per render. The refs are synced in their own effect — declared FIRST, so it
  // has already run by the time the polling effect starts on mount.
  const fetcherRef = useRef(fetcher)
  const terminalRef = useRef(isTerminal)
  useEffect(() => {
    fetcherRef.current = fetcher
    terminalRef.current = isTerminal
  })

  useEffect(() => {
    if (!key) return
    const controller = new AbortController()
    let active = true

    const tick = async () => {
      try {
        const next = await fetcherRef.current(controller.signal)
        if (!active) return
        setData(next)
        setError(null)
        if (!terminalRef.current(next)) {
          timer.current = setTimeout(() => void tick(), INTERVAL_MS)
        }
      } catch (cause) {
        if (!active || controller.signal.aborted) return
        setError(cause instanceof Error ? cause.message : String(cause))
        timer.current = setTimeout(() => void tick(), INTERVAL_MS * 2)
      }
    }

    void tick()

    return () => {
      active = false
      controller.abort()
      if (timer.current) clearTimeout(timer.current)
    }
  }, [key])

  return { data, error, polling: data ? !isTerminal(data) : Boolean(key) }
}
